import { useCallback } from "react";
import { Drawer, styled } from "@mui/material";
import { useDrawer } from "@hooks/useDrawer";
import { useConfirmationModalStore } from "@stores/confirmationModalStore";
import { DrawerHeader } from "./DrawerHeader";
import { getDrawerForm } from "./drawerFormRegistry";

/**
 * The single, app-wide side drawer. Mounted once in the authenticated shell, it
 * derives which form to show from the URL (via `useDrawer`) and renders it from
 * the form registry — so it stays feature-agnostic.
 *
 * Closing: a successful save closes directly (the form calls `useDrawer().close`
 * unguarded). Cancel and backdrop dismiss go through `requestClose`, which warns
 * when the form has unsaved edits — the same guard the app had before. The
 * browser Back button simply clears the drawer's URL params and dismisses it.
 */
export const DrawerHost = () => {
  const { isOpen, context, close } = useDrawer();
  const {
    touched,
    setTouch,
    handleOpen: openConfirm,
    handleClose: closeConfirm,
    setOnOK,
  } = useConfirmationModalStore();

  const form = context ? getDrawerForm(context) : undefined;

  const requestClose = useCallback(() => {
    if (touched) {
      setOnOK(() => {
        setTouch(false);
        closeConfirm();
        close();
      });
      openConfirm();
      return;
    }
    setTouch(false);
    close();
  }, [touched, setOnOK, setTouch, closeConfirm, openConfirm, close]);

  return (
    <Drawer anchor="right" open={isOpen} onClose={requestClose}>
      <SideDrawerContainer>
        <DrawerHeader
          title={form?.title}
          subtitle={form?.subtitle}
          onClose={requestClose}
        />
        {form?.render({ handleClose: requestClose })}
      </SideDrawerContainer>
    </Drawer>
  );
};

const SideDrawerContainer = styled("div")(({ theme }) => ({
  width: "100vw",
  position: "relative",
  height: "100vh",
  display: "flex",
  flexDirection: "column",
  backgroundColor: theme.palette.background.default,
  [theme.breakpoints.up("lg")]: {
    width: "500px",
  },
}));
